import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import API_BASE_URL from '../config';
import Toast from '../components/common/Toast';
import './WishlistPage.css';

const WishlistPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Helper function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/assets/product-placeholder.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    // Remove any duplicate /assets/assets
    const cleanPath = imagePath.replace('/assets/assets/', '/assets/');
    return cleanPath;
  };

  // Show toast message
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/wishlist', message: 'Please login to view your wishlist' } });
      return;
    }
    fetchWishlist();
  }, [isAuthenticated, navigate]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/wishlist`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWishlistItems(data.data || []);
      } else {
        setWishlistItems([]);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId, variantId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          product_id: productId,
          variant_id: variantId
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setWishlistItems(prev => prev.filter(item => 
          !(item.product_id === productId && item.variant_id === variantId)
        ));
        showToast('Removed from wishlist', 'success');
      } else {
        showToast(data.message || 'Failed to remove item', 'error');
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showToast('Error removing item', 'error');
    }
  };

  const moveToCart = async (item) => {
    try {
      // First add to cart
      const cartResponse = await fetch(`${API_BASE_URL}/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          product_id: item.product_id,
          product_name: item.product_name,
          variant_id: item.variant_id,
          variant_name: item.variant_value,
          variant_color: item.variant_color_code,
          quantity: 1,
          price: item.sale_price || item.price,
          image: item.image_url
        })
      });

      const cartData = await cartResponse.json();
      
      if (cartResponse.ok && cartData.success) {
        // Then remove from wishlist
        await removeFromWishlist(item.product_id, item.variant_id);
        showToast('Item moved to cart', 'success');
      } else {
        showToast(cartData.message || 'Failed to move item to cart', 'error');
      }
    } catch (error) {
      console.error('Error moving to cart:', error);
      showToast('Error moving item to cart', 'error');
    }
  };

  const formatPrice = (price) => {
    return `₹${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="wishlist-page">
        <div className="container">
          <div className="loading-spinner">Loading wishlist...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="container">
        <h1 className="page-title">
          <span className="title-line">My Wishlist</span>
          <span className="title-line highlight"></span>
        </h1>

        {wishlistItems.length === 0 ? (
          <div className="empty-wishlist">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <h3>Your wishlist is empty</h3>
            <p>Save your favorite items to buy later</p>
            <Link to="/products" className="btn-primary">Discover Products</Link>
          </div>
        ) : (
          <div className="wishlist-grid">
            {wishlistItems.map(item => (
              <div key={item.id} className="wishlist-card">
                <div className="wishlist-image">
                  <img 
                    src={getImageUrl(item.image_url)} 
                    alt={item.product_name}
                    onError={(e) => {
                      e.target.src = '/assets/product-placeholder.jpg';
                    }}
                  />
                  <button 
                    className="remove-btn"
                    onClick={() => removeFromWishlist(item.product_id, item.variant_id)}
                    title="Remove from wishlist"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                
                <div className="wishlist-details">
                  <h3 className="wishlist-title">{item.product_name}</h3>
                  
                  {item.variant_value && (
                    <p className="wishlist-variant">Color: {item.variant_value}</p>
                  )}
                  
                  <div className="wishlist-price">
                    <span className="current-price">{formatPrice(item.sale_price || item.price)}</span>
                    {item.sale_price && (
                      <span className="original-price">{formatPrice(item.price)}</span>
                    )}
                  </div>
                  
                  <div className="wishlist-actions">
                    <button 
                      className="move-to-cart-btn"
                      onClick={() => moveToCart(item)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="9" cy="21" r="1"/>
                        <circle cx="20" cy="21" r="1"/>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                      </svg>
                      Move to Cart
                    </button>
                    
                    <Link to={`/products/${item.product_id}`} className="view-product">
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast Message */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </div>
  );
};

export default WishlistPage;